import { Controller, useFormContext } from "react-hook-form";
import type { MediaFilterData, MediaFormFields } from "~/features/mediaLibrary/types/mediaLibraryFormTypes";
import MediaFilter from "../MediaFilter/MediaFilter";
import { useTranslation } from "react-i18next";
import { Box, Button, useTheme } from "~/common/mui";
import { Chip } from "~/common/mui/Chip/Chip";
import CheckIcon from "@mui/icons-material/Check";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import AutorenewIcon from "@mui/icons-material/Autorenew";

export default function MediaLibraryFilters(props: { filterOptions: MediaFilterData; sds?: boolean }) {
  const { control, reset } = useFormContext<MediaFormFields>();
  const { filterOptions, sds } = props;
  const { mediaTypes, productPortfolios, languages, segments, country } = filterOptions;

  const { t } = useTranslation();
  const theme = useTheme();

  const handleReset = () => {
    reset({
      q: "",
      mediaTypes: [],
      productPortfolios: [],
      languages: [],
      segments: [],
      country: [],
      fetchFavourite: false,
      showLatestVersion: true,
      sortOrder: "asc",
    });
  };

  return (
    <Box
      sx={{
        mt: 3,
        display: "flex",
        flexDirection: "row",
        gap: 1,
        width: 1,
        maxWidth: 1,
        overflowX: "hidden",
        [theme.breakpoints.down("desktop-m")]: {
          flexDirection: "column",
          alignItems: "flex-start",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          alignItems: "center",
          maxWidth: 1,
        }}
      >
        {sds && (
          <Controller
            name="country"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <MediaFilter label={t("Country")} options={country ?? []} selectedValues={value} onChange={onChange} />
              );
            }}
          />
        )}
        {!sds && (
          <>
            <Controller
              name="mediaTypes"
              control={control}
              render={({ field: { value, onChange } }) => (
                <MediaFilter
                  label={t("media.library.filterLabel.mediaType")}
                  options={mediaTypes}
                  selectedValues={value}
                  onChange={onChange}
                />
              )}
            />

            <Controller
              name="productPortfolios"
              control={control}
              render={({ field: { value, onChange } }) => (
                <MediaFilter
                  label={t("media.library.filterLabel.productCategory")}
                  options={productPortfolios}
                  selectedValues={value}
                  onChange={onChange}
                />
              )}
            />
          </>
        )}
        <Controller
          name="languages"
          control={control}
          render={({ field: { value, onChange } }) => (
            <MediaFilter
              label={t("media.library.filterLabel.language")}
              options={languages}
              selectedValues={value}
              onChange={onChange}
            />
          )}
        />

        {!sds && (
          <>
            <Controller
              name="segments"
              control={control}
              render={({ field: { value, onChange } }) => (
                <MediaFilter
                  label={t("media.library.filterLabel.sector")}
                  options={segments}
                  selectedValues={value}
                  onChange={onChange}
                />
              )}
            />
          </>
        )}

        {userIsLoggedIn() && (
          <>
            <Controller
              name="fetchFavourite"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Chip
                  variant={"filter"}
                  onClick={() => onChange(!value)}
                  icon={value ? <CheckIcon /> : undefined}
                  label={t("media.library.showOnlyFavourite")}
                  selected={value}
                />
              )}
            />
          </>
        )}

        {sds && (
          <>
            <Controller
              name="showLatestVersion"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Chip
                  variant={"filter"}
                  onClick={() => onChange(!value)}
                  icon={!value ? <CheckIcon /> : undefined}
                  label={t("sds.older.version")}
                  selected={!value}
                />
              )}
            />
          </>
        )}
      </Box>

      <Button
        data-test-id={"find-partner"}
        variant="text"
        onClick={handleReset}
        sx={{ borderRadius: 1, flexShrink: 0 }}
        startIcon={<AutorenewIcon />}
      >
        {t("searchPage.refinement.button.clearAll")}
      </Button>
    </Box>
  );
}
